<template>
  <div :id="content.idHTML" :class="jel_getClassObject(content)">
    <!-- Spinner full-page pendant le traitement -->
    <div v-if="showFullPageSpinner" class="full-page-spinner-overlay">
      <div class="spinner-container">
        <b-spinner variant="primary" label="Chargement..."></b-spinner>
        <p class="mt-3">{{ inProgress }}</p>
      </div>
    </div>

    <!-- Champ de recherche Adresse (optionnel) -->
    <AdresseRecherche
      v-if="showAddressSearch"
      ref="addressSearchInput"
      :label="content.libRechercheAdresse || 'Rechercher d\'adresse'"
      :no-results-message="content.noResultsMessage || 'Aucune adresse trouvée'"
      :language="currentLanguage"
      :is-service-enabled="isAddressFindEnabled"
      class="mb-1"
      :disabled="isSubmitting"
      @select="handleAddressSelect"
      @error="handleAddressFindError"
    />

    <!-- Séparateur -->
    <hr v-if="showAddressSearch && showSearchSeparator" class="my-2" />

    <ValidationObserver ref="observer" tag="div">
      <!-- Numéro civique et Rue -->
      <ValidationProvider
        v-slot="validationContext"
        :name="content.libNumeroEtRue"
        rules="required|sans_case_postale|is_valid_address|compteValidation:adresse"
        mode="aggressive"
        tag="div"
      >
        <label>{{ content.libAdresse }}</label>
        <b-form-group label-for="numeroEtRue" class="has-float-label mt-1">
          <b-form-input
            id="numeroEtRue"
            v-model="numeroEtRue"
            v-capitalize
            v-remove-extra-spaces
            trim
            autocomplete="street-address"
            maxlength="52"
            class="mt-0"
            :placeholder="content.libNumeroEtRue"
            :state="getValidationState(validationContext)"
            :disabled="isSubmitting"
          />
          <label for="numeroEtRue" class="pl-0">{{ content.libNumeroEtRue }}</label>
          <ErreurValidation
            id="numeroEtRue-live-feedback"
            :message="validationContext.errors[0]"
          ></ErreurValidation>
        </b-form-group>
      </ValidationProvider>

      <!-- Appartement (Optionnel) -->
      <ValidationProvider
        v-slot="validationContext"
        :name="content.libAppartement"
        rules="sans_case_postale|compteValidation:appartement"
        mode="aggressive"
        tag="div"
      >
        <b-form-group label-for="appartement" class="has-float-label">
          <b-form-input
            id="appartement"
            v-model="appartement"
            v-capitalize
            v-remove-extra-spaces
            trim
            maxlength="8"
            class="mt-0"
            :placeholder="content.libAppartement"
            :state="getValidationState(validationContext)"
            :disabled="isSubmitting"
          />
          <label for="appartement" class="pl-0">{{ content.libAppartement }}</label>
          <ErreurValidation
            id="appartement-live-feedback"
            :message="validationContext.errors[0]"
          ></ErreurValidation>
        </b-form-group>
      </ValidationProvider>

      <!-- Ville -->
      <ValidationProvider
        v-slot="validationContext"
        :name="content.libVille"
        rules="required|min:3|sans_case_postale|compteValidation:ville"
        mode="aggressive"
        tag="div"
      >
        <b-form-group :label="content.libVille" label-for="ville">
          <b-form-input
            id="ville"
            v-model="ville"
            v-capitalize
            v-remove-extra-spaces
            trim
            autocomplete="address-level2"
            required
            maxlength="40"
            :state="getValidationState(validationContext)"
            :disabled="isSubmitting"
          />
          <ErreurValidation
            id="ville-live-feedback"
            :message="validationContext.errors[0]"
            :variables="variablesARemplacer"
          ></ErreurValidation>
        </b-form-group>
      </ValidationProvider>

      <!-- Code postal et Province -->
      <b-row>
        <div class="col-6">
          <CodePostal :content="content" :disabled="isSubmitting" />
        </div>

        <div class="d-flex flex-column align-items-start col-6">
          <div class="mb-3 mb-md-2 font-weight-bold">
            {{ content.libProvince }}
          </div>
          <div>{{ content.libQuebec }}</div>
        </div>
      </b-row>
    </ValidationObserver>

    <!-- Modale de confirmation d'adresse (tous les modes) -->
    <AdresseConfirmationModale
      v-if="showConfirmationModal"
      class="compteClair"
      :visible="showConfirmationModal"
      :mode="modalMode"
      :labels="modalLabels"
      :entered-address="enteredAddress"
      :suggested-address="modalSuggestedAddress"
      :validation-results="modalValidationResults"
      :show-validation-dropdown="showModalValidationDropdown"
      @close="handleModalClose"
      @continue="handleModalContinue"
      @continue-without-validation="handleContinueWithoutValidation"
      @modify-address="handleModifyAddress"
      @address-select="handleModalAddressSelect"
      @update:selectedAddress="handleSelectedAddressChange"
    />
  </div>
</template>

<script>
// Services
import adresseService from '@/services/api/compte/adresse';
// Composants
import ErreurValidation from '@/components/formulaire/compte/ErreurValidation';
import CodePostal from '@/components/formulaire/compte/CodePostal';
import AdresseRecherche from '@/components/formulaire/compte/AdresseRecherche.vue';
import AdresseConfirmationModale from '@/components/formulaire/compte/AdresseConfirmationModale.vue';
// Helpers
import { compareAddresses, parseAddressLine, formatAddress } from '@/helpers/AddressHelpers';

export default {
  name: 'Adresse',

  components: {
    AdresseRecherche,
    CodePostal,
    ErreurValidation,
    AdresseConfirmationModale,
  },

  props: {
    content: {
      type: Object,
      required: true,
    },
    showAddressSearch: {
      type: Boolean,
      default: true,
    },
    showSearchSeparator: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isSubmitting: false,
      isUserTyping: false,
      isAutoFilling: false,
      showConfirmationModal: false,
      showServiceErrorModal: false,
      validationStatus: null, // V, C, N, X, Z
      isAddressFindEnabled: true,
      addressVerified: false,
      modalMode: 'validation', // 'validation', 'not-validated', 'service-unavailable'
      modalSuggestedAddress: null,
      modalSelectedAddressType: 'suggested', // 'entered' ou 'suggested' - par défaut l'adresse suggérée
      variablesARemplacer: {
        minLength: 3,
      },
      showFullPageSpinner: false,
      initialAddressReference: null,
      selectedAddressReference: null,
    };
  },

  computed: {
    numeroEtRue: {
      get() {
        return this.$store.state.compte.adresse.ligneAdresse;
      },
      set(value) {
        if (this.isAutoFilling) return;

        // Utiliser le helper parseAddressLine pour parser la valeur
        const { numero, rue, ligneAdresse } = parseAddressLine(value);

        this.$store.commit('compte/SET_NUMERO', numero);
        this.$store.commit('compte/SET_RUE', rue);
        this.$store.commit('compte/SET_LIGNE_ADRESSE', ligneAdresse);
        // avant de refaire la validation
        this.$store.commit('compte/SET_CHOIX_ADRESSE_VALIDATION', '');
        this.$store.commit('compte/SET_VALIDATION_ADRESSE', '');

        this.isUserTyping = true;
      },
    },

    appartement: {
      get() {
        return this.$store.state.compte.adresse.appartement;
      },
      set(value) {
        this.$store.commit('compte/SET_APPARTEMENT', value);
        this.isUserTyping = true;
      },
    },

    ville: {
      get() {
        return this.$store.state.compte.adresse.ville;
      },
      set(value) {
        this.$store.commit('compte/SET_VILLE', value);
        this.isUserTyping = true;
      },
    },

    currentLanguage() {
      return this.$i18n?.locale || 'fr';
    },

    inProgress() {
      return this.$i18n?.locale === 'en' ? 'In progress...' : 'Traitement en cours...';
    },

    enteredAddress() {
      return {
        numero: this.$store.state.compte.adresse.numero,
        rue: this.$store.state.compte.adresse.rue,
        appartement: this.$store.state.compte.adresse.appartement,
        ville: this.$store.state.compte.adresse.ville,
        codePostal: this.$store.state.compte.adresse.codePostal,
        province: 'QC',
      };
    },

    suggestedAddress() {
      return this.$store.state.compte.suggestedAddress.fullAddress;
    },

    /**
     * Labels pour la AdresseConfirmationModale (tous les modes)
     */
    modalLabels() {
      return {
        // Mode validation
        confirmAddress:
          this.content.confirmAddressTitle || "Oups! Nous n'avons pas pu trouvé l'adresse exacte",
        descriptionConfirmAddress: this.content.confirmAddressText,
        enteredAddress: this.content.enteredAddress || 'Adresse saisie',
        suggestedAddress: this.content.suggestedAddress || 'Adresse suggérée',
        btnRegisterAndContinue: this.content.btnRegisterAndContinue || 'Enregistrer et continuer',

        // Mode not-validated
        notValidatedTitle:
          this.content.notValidatedTitle || "Oups! Nous n'avons pas trouvé cette adresse",
        notValidatedMessage:
          this.content.notValidatedMessage ||
          "L'adresse que vous avez saisie n'a pas pu être vérifiée.",
        notValidatedInfo:
          this.content.notValidatedInfo ||
          "Assurez-vous que votre adresse ne contient pas d'erreur.",

        // Mode service-unavailable
        serviceUnavailableTitle:
          this.content.serviceUnavailableTitle ||
          'Oups! Nous ne sommes pas en mesure de vérifier votre adresse',
        serviceUnavailableMessage:
          this.content.serviceUnavailableMessage || 'Vérifiez-la avant de continuer.',
        serviceUnavailableInfo:
          this.content.serviceUnavailableInfo ||
          "Notre outil de vérification n'est pas disponible.",

        // Communs
        addressEnteredLabel: this.content.addressEnteredLabel || 'Adresse saisie',
        btnContinueAndSave: this.content.btnContinueAndSave || 'Continuer avec cette adresse',
        btnModifyAddress: this.content.btnModifyAddress || "Modifier l'adresse",
        noSuggestedAddress: this.content.noSuggestedAddress || 'Aucune adresse suggérée',
      };
    },

    /**
     * Résultats de validation pour la modale (vide pour l'instant)
     */
    modalValidationResults() {
      return [];
    },

    /**
     * Afficher le dropdown de validation dans la modale
     */
    showModalValidationDropdown() {
      return false;
    },
  },

  watch: {
    isUserTyping(newVal) {
      if (newVal) {
        // Réinitialiser après un délai pour permettre la soumission
        setTimeout(() => {
          this.isUserTyping = false;
        }, 500);
      }
    },
  },

  mounted() {
    this.initialAddressReference = this.createAddressReference(this.enteredAddress);

    // Effacer l'adresse du localStorage au chargement du formulaire
    try {
      localStorage.removeItem('retrieved_address');
    } catch (error) {
      // Erreur silencieuse - pas critique
    }

    // Intercepter la soumission du formulaire parent en utilisant l'évènement DOM 'click' sur le bouton submit
    this.$nextTick(() => {
      const parentForm = this.findParentForm();
      if (parentForm) {
        // Trouver le bouton submit du formulaire parent
        const submitButton = parentForm.querySelector('button[type="submit"]');
        if (submitButton) {
          // Utiliser la phase de capture pour intercepter AVANT le handler Vue
          submitButton.addEventListener('click', this.handleSubmitButtonClick, true);
        }
      }
    });
  },

  beforeDestroy() {
    // Retirer le listener avant la destruction du composant
    this.$nextTick(() => {
      const parentForm = this.findParentForm();
      if (parentForm) {
        const submitButton = parentForm.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.removeEventListener('click', this.handleSubmitButtonClick, true);
        }
      }
    });
  },

  methods: {
    getValidationState({ dirty, validated, valid = null }) {
      // Uniquement si la validation a été effectuée une fois et que le champ est invalide
      if (validated && !valid) {
        return valid;
      }
      return null;
    },

    createAddressReference(address) {
      if (!address) return null;

      return {
        numero: address.numero || '',
        rue: address.rue || '',
        appartement: address.appartement || '',
        complementAdresse: '',
        ville: address.ville || '',
        codePostal: this.normalizePostalCode(address.codePostal),
        province: address.province || 'QC',
      };
    },

    normalizePostalCode(codePostal) {
      return (codePostal || '').toString().replace(/\W/g, '').toUpperCase();
    },

    isCurrentAddressSameAs(referenceAddress) {
      if (!referenceAddress) {
        return false;
      }

      return compareAddresses(this.createAddressReference(this.enteredAddress), referenceAddress);
    },

    getStoredAddressReference() {
      try {
        const storedAddress = localStorage.getItem('retrieved_address');
        return storedAddress ? this.createAddressReference(JSON.parse(storedAddress)) : null;
      } catch (error) {
        return null;
      }
    },

    getProfileAddressReference() {
      const profileAddress = this.$store.state.profile?.address;
      if (!profileAddress) {
        return null;
      }

      return this.createAddressReference({
        numero: profileAddress.addressLine1,
        rue: profileAddress.addressLine2,
        appartement: profileAddress.addressLine3,
        ville: profileAddress.city,
        codePostal: profileAddress.postcode,
        province: profileAddress.state || 'QC',
      });
    },

    shouldSkipAddressVerify() {
      return [
        this.initialAddressReference,
        this.selectedAddressReference,
        this.getProfileAddressReference(),
        this.getStoredAddressReference(),
      ].some((referenceAddress) => this.isCurrentAddressSameAs(referenceAddress));
    },

    isAddressSameAsRetrieved() {
      return this.isCurrentAddressSameAs(this.getStoredAddressReference());
    },

    async onSubmit() {
      this.isSubmitting = true;
      // Par défaut, le client n'a refusé aucune proposition d'adresse.
      // Cette valeur passe à true si le client conserve son adresse saisie
      // plutôt qu'une proposition Loqate, ou si Loqate ne trouve pas l'adresse.
      this.$store.commit('compte/SET_IND_REFUS_PROPOSITION_ADRESSE', false);

      try {
        if (this.shouldSkipAddressVerify()) {
          // Adresse inchangée depuis le profil ou depuis la sélection d'adresse :
          // pas d'appel Verify.
          this.continueWithAddress(this.enteredAddress);
          return;
        }

        // Appel au service AddressVerify pour valider l'adresse
        const verificationResult = await adresseService.verifierAdresse();

        // Gérer les différents statuts de réponse
        this.handleVerificationStatus(verificationResult);
      } catch (error) {
        // En cas d'erreur, afficher la modale d'indisponibilité du service
        this.showServiceUnavailableModal();
      } finally {
        this.isSubmitting = false;
      }
    },

    /**
     * Gère les différents statuts de reponse du service AddressVerify
     * @param {Object} verificationResult - Résultat de la vérification d'adresse
     */
    handleVerificationStatus(verificationResult) {
      const { validationStatus, validated, suggestedAddress, exactMatch } = verificationResult;

      // Cas spécifique : ERROR avec validated=false et suggestedAddress=null
      // Cela indique une erreur du service AddressVerify
      if (validationStatus === 'ERROR' && validated === false && suggestedAddress === null) {
        this.showServiceUnavailableModal();
        return;
      }

      switch (validationStatus) {
        case 'SUCCESS':
          // Requête complétée avec succès
          if (!validated) {
            // Adresse non validée - afficher modale
            this.showAddressNotValidatedModal();
            break;
          }

          // Match parfait ou pas d'adresse suggérée - continuer directement
          if (exactMatch || !suggestedAddress) {
            this.continueWithAddress(this.enteredAddress);
            break;
          }

          // Adresse suggérée présente - vérifier si elle diffère de l'adresse saisie
          const normalizedSuggestedAddress = formatAddress(suggestedAddress);
          if (this.doAddressesDiffer(this.enteredAddress, normalizedSuggestedAddress)) {
            // Afficher modale de confirmation avec les deux adresses
            this.showAddressComparisonModal(suggestedAddress);
          } else {
            // Les adresses sont identiques - continuer directement
            this.continueWithAddress(this.enteredAddress);
          }
          break;

        case 'PARTIAL_MATCH':
          // Correspondance partielle - afficher modale avec adresse suggérée
          if (suggestedAddress) {
            this.showAddressComparisonModal(suggestedAddress);
          } else {
            this.showAddressNotValidatedModal();
          }
          break;

        case 'NOT_FOUND':
          // Adresse non trouvée - afficher modale
          this.showAddressNotValidatedModal();
          break;

        case 'ERROR':
        default:
          // Erreur ou statut inconnu - afficher popup d'indisponibilité du service
          this.showServiceUnavailableModal();
          break;
      }
    },

    /**
     * Continue avec l'adresse fournie (sauvegarde et poursuit le parcours)
     * Re-soumet le formulaire parent pour que Formulaire.vue exécute la tâche
     * Magnolia (ex: sauvegarderAdresseEtValiderIdentite).
     */
    continueWithAddress(address) {
      // Vérifier si le résultat de la vérification d'adresse contient une erreur
      const verificationResult = this.$store.state.compte.verificationAdresse;
      if (verificationResult && verificationResult.error) {
        this.isSubmitting = false;
        this.showFullPageSpinner = false;
        return;
      }

      // Mettre à jour le store avec l'adresse choisie
      this.$store.commit('compte/SET_CHOIX_ADRESSE_VALIDATION', address);

      // Notifier le parent si besoin
      this.$emit('submit', address);

      // Afficher le spinner avant d'appeler la tâche Magnolia configurée
      this.showFullPageSpinner = true;

      // Marquer l'adresse comme vérifiée et re-soumettre le formulaire parent
      // pour que Formulaire.vue exécute la tâche Magnolia configurée
      this.addressVerified = true;
      const parentForm = this.findParentForm();
      if (parentForm) {
        const submitButton = parentForm.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.click();
        }
      }
    },

    /**
     * Affiche la modale de comparaison d'adresses
     * (adresse saisie vs adresse suggérée)
     */
    showAddressComparisonModal(suggestedAddress) {
      // Préparer l'adresse suggérée au format attendu par la modale
      // S'assurer que tous les champs sont des chaînes (jamais null/undefined)
      this.modalSuggestedAddress = {
        fullAddress: suggestedAddress.fullAddress || '',
        numero: suggestedAddress.noCivique || '',
        rue: suggestedAddress.rue || '',
        appartement: suggestedAddress.noAppartement || '',
        complementAdresse: suggestedAddress.complementAdresse || '',
        ville: suggestedAddress.ville || '',
        codePostal: suggestedAddress.codePostal || '',
        province: suggestedAddress.province || 'QC',
        pays: suggestedAddress.pays || 'CA',
      };

      this.modalSelectedAddressType = 'suggested';
      this.modalMode = 'validation';
      this.showConfirmationModal = true;
    },

    /**
     * Affiche la modale indiquant que l'adresse n'a pas pu être vérifiée
     */
    showAddressNotValidatedModal() {
      this.modalSuggestedAddress = null;
      this.modalSelectedAddressType = 'entered';
      this.modalMode = 'not-validated';
      this.showConfirmationModal = true;
    },

    /**
     * Affiche la modale d'indisponibilité du service Address
     */
    showServiceUnavailableModal() {
      this.modalSuggestedAddress = null;
      this.modalSelectedAddressType = 'entered';
      this.modalMode = 'service-unavailable';
      this.showConfirmationModal = true;
    },

    handleModalClose() {
      this.showConfirmationModal = false;
    },

    /**
     * Gère l'évènement "continue" de la AdresseConfirmationModale (mode validation)
     * L'utilisateur a choisi une adresse (saisie ou suggérée)
     */
    handleModalContinue(selectedAddressType) {
      this.continueWithModalAddressChoice(selectedAddressType);
    },

    /**
     * Gère le changement de sélection d'adresse (saisie vs suggérée) dans la modale
     */
    handleSelectedAddressChange(value) {
      this.modalSelectedAddressType = value;
    },

    /**
     * Gère la sélection d'une adresse dans le dropdown de la modale
     */
    handleModalAddressSelect() {
      // Pour l'instant, on sauvegarde juste la sélection
      // Cette fonctionnalité sera implémentée si nécessaire avec les résultats
    },

    handleServiceErrorClose() {
      this.showServiceErrorModal = false;
    },

    /**
     * Gère l'évènement "continuer sans validation" de la AdresseConfirmationModale
     * L'utilisateur choisit de continuer avec l'adresse sélectionnée (saisie ou suggérée)
     */
    handleContinueWithoutValidation(selectedAddressType) {
      this.continueWithModalAddressChoice(selectedAddressType);
    },

    continueWithModalAddressChoice(selectedAddressType = this.modalSelectedAddressType) {
      const acceptsProposal =
        this.modalMode === 'validation' &&
        selectedAddressType === 'suggested' &&
        !!this.modalSuggestedAddress;
      const rejectsAddressProposal =
        this.modalMode === 'not-validated' || (this.modalMode === 'validation' && !acceptsProposal);

      this.$store.commit('compte/SET_IND_REFUS_PROPOSITION_ADRESSE', rejectsAddressProposal);

      this.showConfirmationModal = false;

      const selectedAddress = acceptsProposal ? this.modalSuggestedAddress : this.enteredAddress;

      this.continueWithAddress(selectedAddress);
    },

    /**
     * Gère l'événement "modifier l'adresse" de la AdresseConfirmationModale
     * L'utilisateur choisit de revenir à la saisie pour modifier son adresse
     */
    handleModifyAddress() {
      this.showConfirmationModal = false;
      // La modale se ferme, l'utilisateur peut modifier son adresse
    },

    /**
     * Gère la selection d'une adresse
     * Appelé quand l'utilisateur sélectionne une adresse dans AdresseRecherche
     */
    handleAddressSelect(addressData) {
      const { formatted } = addressData;

      // Activer le flag d'auto-remplissage pour éviter les effets de bord
      this.isAutoFilling = true;

      // Mettre à jour le store Vuex avec toutes les données de l'adresse
      // On utilise les mutations individuelles pour chaque champ
      this.$store.commit('compte/SET_NUMERO', formatted.numero || '');
      this.$store.commit('compte/SET_RUE', formatted.rue || '');
      this.$store.commit('compte/SET_LIGNE_ADRESSE', formatted.ligneAdresse || '');
      this.$store.commit('compte/SET_APPARTEMENT', formatted.appartement || '');
      this.$store.commit('compte/SET_VILLE', formatted.ville || '');
      this.$store.commit('compte/SET_CODE_POSTAL', formatted.codePostal || '');
      this.$store.commit('compte/SET_ADRESSESUGGEREE_FLAG', true);
      this.$store.commit('compte/SET_IND_REFUS_PROPOSITION_ADRESSE', false);
      this.selectedAddressReference = this.createAddressReference(formatted);

      // Reset validation pour forcer une nouvelle validation avec l'adresse
      this.$store.commit('compte/SET_CHOIX_ADRESSE_VALIDATION', '');
      this.$store.commit('compte/SET_VALIDATION_ADRESSE', '');

      // Vider l'input de recherche après sélection
      if (this.$refs.addressSearchInput) {
        this.$refs.addressSearchInput.clear();
      }

      // Forcer la réactivité de Vue en utilisant $forceUpdate
      this.$nextTick(() => {
        this.$forceUpdate();
      });

      // Désactiver le flag après un court délai pour permettre les futures modifications
      setTimeout(() => {
        this.isAutoFilling = false;
      }, 100);
    },

    /**
     * Gère les erreurs du service AddressFind
     */
    handleAddressFindError() {
      // Toute erreur AddressFind rend le service indisponible côté UI.
      if (this.$refs.addressSearchInput) {
        this.$refs.addressSearchInput.clear();
      }
      this.isAddressFindEnabled = false;

      // Afficher un message d'erreur à l'utilisateur si nécessaire
      // this.$emit('address-find-error', error);
    },

    /**
     * Trouve le formulaire parent (<form> ou <b-form>)
     * @returns {HTMLFormElement|null}
     */
    findParentForm() {
      let el = this.$el;
      while (el && el.parentElement) {
        el = el.parentElement;
        if (el.tagName === 'FORM') {
          return el;
        }
      }
      return null;
    },

    /**
     * Compare deux adresses pour déterminer si elles diffèrent de manière significative
     * @param {Object} enteredAddress - Adresse saisie par l'utilisateur
     * @param {Object} suggestedAddress - Adresse suggérée par le service de Vérification d'Adresse
     * @returns {boolean} true si les adresses diffèrent, false sinon
     */
    doAddressesDiffer(enteredAddress, suggestedAddress) {
      // Utilise compareAddresses() qui retourne true si identiques
      // On inverse donc le résultat pour retourner true si elles diffèrent
      return !compareAddresses(enteredAddress, suggestedAddress);
    },

    /**
     * Gestionnaire de clic sur le bouton submit du formulaire parent
     * Intercepte le clic pour Vérifier l'Adresse d'abord
     * @param {Event} event - Évenement click du bouton submit
     */
    async handleSubmitButtonClick(event) {
      // Si l'adresse a déjà été vérifiée, laisser le clic remonter
      // pour que Formulaire.vue déclenche la tâche Magnolia configurée
      if (this.addressVerified) {
        this.addressVerified = false;
        return;
      }

      // Valider les champs avec ValidationObserver
      const isValid = await this.$refs.observer.validate();
      if (!isValid) {
        // Ne pas empêcher le clic - laisser la validation du formulaire parent s'exécuter
        return;
      }

      // Empêcher la soumission normale pour faire la vérification d'Adresse d'abord
      event.preventDefault();
      event.stopImmediatePropagation(); // Empêche les autres handlers (y compris Vue)

      // Appeler la logique de Vérification de l'Adresse
      await this.onSubmit();

      // Note: si une modale s'affiche, elle gèrera la suite du processus
      // Sinon continueWithAddress() sera appele automatiquement
    },
  },
};
</script>

<style lang="scss" scoped>
.hauteur {
  padding: 0.5rem;

  @include media-breakpoint-down(md) {
    height: auto !important;
    padding: 0.5rem 0.75rem;
  }
}

// Spinner full-page overlay
.full-page-spinner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;

  .spinner-container {
    display: flex;
    flex-direction: column;
    align-items: center;

    p {
      color: #333;
      font-size: 1.1rem;
      font-weight: 500;
      margin: 0;
    }
  }
}
</style>
